<script setup lang="ts">
import { computed, reactive, ref, useId } from "vue";
import Button from "../../components/Button.vue";
import DatePicker from "../../components/DatePicker.vue";
import Dropdown from "../../components/Dropdown.vue";
import FormField from "../../components/FormField.vue";
import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import { useModalStore } from "../../stores/modalStore.ts";
import {
  buildBlankShippingPo,
  planBatchDisposition,
  type BatchDispositionResult,
  type BatchDispositionSelection,
  type DispositionCandidate,
  type ReleasedLine,
} from "./shippingPoEditing.ts";

/**
 * UX-17 combined disposition dialog. Released units (from a PO deletion, a
 * SKU line removal, or a quantity reduction) go to leftover inventory or are
 * split across user-selected same-division POs proportionally to each
 * target's spare cube. Capacity is mandatory for every target type: units
 * beyond a target's measurable spare cube require that target's explicit
 * "Allow over capacity" acknowledgment, and any remainder needs an explicit
 * choice of a new PO or acknowledged over-capacity targets. Otherwise the
 * remainder defaults to leftover inventory.
 */
const props = defineProps<{
  context: "delete-po" | "reduce-line" | "remove-line";
  sourcePoSequence: number;
  releasedLines: ReleasedLine[];
  candidates: DispositionCandidate[];
  newPoDivision: string;
  newPoCubeCapacityFt3: number;
  newPoDefaults: {
    deliverTo: string;
    shipDateValue: Date | undefined;
    arrivalDate: Date | undefined;
  };
  newPoWarehouseOptions: DropdownOption[];
  onConfirm: (result: BatchDispositionResult) => void;
  onCancel: () => void;
}>();

const modalStore = useModalStore();
const newPoFieldsId = useId();
const destination = ref<"leftover" | "targets" | "new-po">("leftover");
const selectedByPoId = reactive<Record<string, boolean>>({});
const overCapacityAckByPoId = reactive<Record<string, boolean>>({});
const remainderChoice = ref<"leftover" | "over-capacity" | "new-po">(
  "leftover"
);
const newPoDeliverTo = ref(props.newPoDefaults.deliverTo);
const newPoShipDate = ref(
  props.newPoDefaults.shipDateValue
    ? new Date(props.newPoDefaults.shipDateValue)
    : undefined
);
const newPoArrivalDate = ref(
  props.newPoDefaults.arrivalDate
    ? new Date(props.newPoDefaults.arrivalDate)
    : undefined
);

const totalReleasedQty = computed(() =>
  props.releasedLines.reduce((sum, line) => sum + line.qty, 0)
);

const summary = computed(() => {
  const units = `${String(totalReleasedQty.value)} unit${
    totalReleasedQty.value === 1 ? "" : "s"
  }`;
  if (props.context === "delete-po") {
    const skuCount = props.releasedLines.length;
    return `Deleting PO ${String(props.sourcePoSequence)} releases ${units} across ${String(skuCount)} SKU${skuCount === 1 ? "" : "s"}. Choose where they go before the PO is deleted.`;
  }
  const sku = props.releasedLines[0]?.sku ?? "";
  const verb = props.context === "remove-line" ? "Removing" : "Reducing";
  const preposition = props.context === "remove-line" ? "from" : "on";
  return `${verb} SKU ${sku} ${preposition} PO ${String(props.sourcePoSequence)} releases ${units}. Choose where they go before this change is applied.`;
});

const selections = computed<BatchDispositionSelection[]>(() =>
  props.candidates
    .filter((candidate) => selectedByPoId[candidate.po.id])
    .map((candidate) => ({
      poId: candidate.po.id,
      allowOverCapacity: Boolean(overCapacityAckByPoId[candidate.po.id]),
    }))
);

const plan = computed(() =>
  planBatchDisposition(
    props.candidates,
    selections.value,
    props.releasedLines,
    destination.value === "targets" &&
      remainderChoice.value === "over-capacity",
    newPoMode.value
      ? { newPo: newPoDraft.value, newPoMode: newPoMode.value }
      : {}
  )
);

const newPoMode = computed<"only" | "remainder" | null>(() => {
  if (destination.value === "new-po") return "only";
  if (destination.value === "targets" && remainderChoice.value === "new-po")
    return "remainder";
  return null;
});

const newPoDraft = computed(() => {
  const po = buildBlankShippingPo(
    "disposition-new-po-preview",
    0,
    props.newPoCubeCapacityFt3,
    props.newPoDivision
  );
  po.deliverTo = newPoDeliverTo.value;
  po.shipDateValue = newPoShipDate.value;
  po.arrivalDate = newPoArrivalDate.value;
  return po;
});

const newPoMetadataError = computed(() => {
  if (!props.newPoDivision.trim()) {
    return "Division is unavailable; a new PO cannot be safely isolated.";
  }
  if (!newPoDeliverTo.value.trim()) {
    return props.newPoWarehouseOptions.length > 0
      ? "Choose a warehouse for the new PO."
      : "Warehouse choices are unavailable; the new-PO route cannot be completed.";
  }
  if (
    !props.newPoWarehouseOptions.some(
      (option) => option.value === newPoDeliverTo.value
    )
  )
    return "Choose an available warehouse in this division for the new PO.";
  if (!newPoShipDate.value || !Number.isFinite(newPoShipDate.value.getTime()))
    return "Choose a ship date for the new PO.";
  if (
    !newPoArrivalDate.value ||
    !Number.isFinite(newPoArrivalDate.value.getTime())
  )
    return "Choose an arrival date for the new PO.";
  if (newPoArrivalDate.value < newPoShipDate.value)
    return "The new PO arrival date cannot be before its ship date.";
  return "";
});

const needsNewPo = computed(() => Boolean(newPoMode.value));

const acknowledgedCount = computed(
  () =>
    selections.value.filter((selection) => selection.allowOverCapacity).length
);

function candidateLabels(candidate: DispositionCandidate): string[] {
  return candidate.labels.map((label) => {
    if (label === "matching-sku") return "Matching SKU";
    if (label === "matching-ship-date")
      return candidate.shipDateMissing ? "No ship date" : "Matching ship date";
    if (label === "has-capacity") return "Fits a complete pack";
    return "Unavailable for normal placement";
  });
}

function spareCubeDisplay(candidate: DispositionCandidate): string {
  return candidate.spareCubeFt3 === undefined
    ? "Unavailable"
    : `${candidate.spareCubeFt3.toFixed(2)} ft³`;
}

function plannedQtyFor(candidate: DispositionCandidate): number {
  return (
    plan.value.perTarget.find((target) => target.po.id === candidate.po.id)
      ?.plannedQty ?? 0
  );
}

function skuPresenceFor(candidate: DispositionCandidate): string {
  return candidate.matchingSkus.length > 0
    ? `Already carries ${candidate.matchingSkus.join(", ")}`
    : "SKU line will be added";
}

function showOverCapacityAck(candidate: DispositionCandidate): boolean {
  return (
    Boolean(selectedByPoId[candidate.po.id]) &&
    (plan.value.capacityShortfallQty > 0 ||
      candidate.spareCubeFt3 === undefined)
  );
}

function destinationsFor(skuPlan: {
  allocations: { po: { id: string; sequence: number }; qty: number }[];
}): string {
  const parts = skuPlan.allocations
    .filter((allocation) => allocation.qty > 0)
    .map((allocation) =>
      allocation.po.id === "disposition-new-po-preview"
        ? `New PO: ${String(allocation.qty)}`
        : `PO ${String(allocation.po.sequence)}: ${String(allocation.qty)}`
    );
  return parts.length > 0 ? parts.join("; ") : "—";
}

const confirmDisabledReason = computed<string>(() => {
  if (destination.value === "leftover") return "";
  if (destination.value === "targets" && selections.value.length === 0)
    return "Select at least one destination PO.";
  if (
    destination.value === "targets" &&
    remainderChoice.value === "over-capacity" &&
    acknowledgedCount.value === 0
  )
    return 'Check "Allow over capacity" on at least one selected PO.';
  if (needsNewPo.value && newPoMetadataError.value !== "")
    return newPoMetadataError.value;
  return "";
});

const confirmButtonText = computed(() =>
  props.context === "delete-po" ? "Delete PO" : "Apply"
);

function handleConfirm(): void {
  if (confirmDisabledReason.value !== "") return;
  modalStore.close();
  const mode = newPoMode.value;
  props.onConfirm({
    selections: destination.value === "targets" ? selections.value : [],
    remainderToLeftover:
      destination.value !== "targets" ||
      remainderChoice.value !== "over-capacity",
    ...(mode
      ? {
          newPoMode: mode,
          newPoMetadata: {
            deliverTo: newPoDeliverTo.value,
            shipDateValue: newPoShipDate.value,
            arrivalDate: newPoArrivalDate.value,
          },
        }
      : {}),
  });
}

function handleCancel(): void {
  props.onCancel();
  modalStore.close();
}
</script>

<template>
  <div class="qty-disposition-modal">
    <div class="modal-header draggable">
      <span class="modal-header-title"
        >Where should the released units go?</span
      >
      <button
        type="button"
        class="modal-header-close"
        aria-label="Close disposition dialog"
        @mousedown.stop
        @click.stop="handleCancel"
      >
        &times;
      </button>
    </div>
    <div class="modal-body qty-disposition-modal-body">
      <p class="qty-disposition-modal-summary">{{ summary }}</p>

      <fieldset class="qty-disposition-modal-options">
        <legend>Destination</legend>
        <label
          class="qty-disposition-modal-option"
          for="qty-disposition-leftover"
        >
          <input
            id="qty-disposition-leftover"
            v-model="destination"
            type="radio"
            name="qty-disposition-destination"
            value="leftover"
          />
          Move everything to leftover inventory
        </label>
        <label
          class="qty-disposition-modal-option"
          for="qty-disposition-targets"
        >
          <input
            id="qty-disposition-targets"
            v-model="destination"
            type="radio"
            name="qty-disposition-destination"
            value="targets"
            :disabled="candidates.length === 0"
          />
          Move to selected POs
          <span
            v-if="candidates.length === 0"
            class="qty-disposition-modal-muted"
          >
            (no other POs in this division)
          </span>
        </label>
        <label
          class="qty-disposition-modal-option"
          for="qty-disposition-new-po"
        >
          <input
            id="qty-disposition-new-po"
            v-model="destination"
            type="radio"
            name="qty-disposition-destination"
            value="new-po"
          />
          Place complete packs in a new PO; anything that does not fit remains
          leftover
        </label>
      </fieldset>

      <template v-if="destination === 'targets'">
        <ul class="qty-disposition-modal-candidates">
          <li
            v-for="candidate in candidates"
            :key="candidate.po.id"
            class="qty-disposition-modal-candidate"
          >
            <label
              class="qty-disposition-modal-option"
              :for="`qty-disposition-target-${candidate.po.id}`"
            >
              <input
                :id="`qty-disposition-target-${candidate.po.id}`"
                v-model="selectedByPoId[candidate.po.id]"
                type="checkbox"
                :aria-label="`Send units to PO ${String(candidate.po.sequence)}`"
              />
              <strong>PO {{ candidate.po.sequence }}</strong>
              <span
                v-for="label in candidateLabels(candidate)"
                :key="label"
                class="qty-disposition-modal-label"
              >
                {{ label }}
              </span>
            </label>
            <div class="qty-disposition-modal-candidate-details">
              <span data-testid="candidate-spare-cube"
                >Spare cube: {{ spareCubeDisplay(candidate) }}</span
              >
              <span data-testid="candidate-planned-qty"
                >Planned: +{{ plannedQtyFor(candidate) }} unit{{
                  plannedQtyFor(candidate) === 1 ? "" : "s"
                }}</span
              >
              <span data-testid="candidate-sku-presence">{{
                skuPresenceFor(candidate)
              }}</span>
            </div>
            <ul
              v-if="candidate.unavailableReasons.length > 0"
              class="qty-disposition-modal-unavailable-reasons"
              :aria-label="`Placement limits for PO ${String(candidate.po.sequence)}`"
            >
              <li v-for="reason in candidate.unavailableReasons" :key="reason">
                {{ reason }}
              </li>
            </ul>
            <label
              v-if="showOverCapacityAck(candidate)"
              class="qty-disposition-modal-option qty-disposition-modal-ack"
              :for="`qty-disposition-ack-${candidate.po.id}`"
            >
              <input
                :id="`qty-disposition-ack-${candidate.po.id}`"
                v-model="overCapacityAckByPoId[candidate.po.id]"
                type="checkbox"
                :aria-label="`Allow PO ${String(candidate.po.sequence)} over capacity`"
              />
              Allow over capacity
            </label>
          </li>
        </ul>
      </template>

      <template v-if="destination === 'targets' || destination === 'new-po'">
        <div
          class="qty-disposition-modal-preview"
          aria-live="polite"
          data-testid="disposition-preview"
        >
          <h3>Per-SKU preview</h3>
          <table class="qty-disposition-modal-preview-table">
            <thead>
              <tr>
                <th scope="col">SKU</th>
                <th scope="col">Released</th>
                <th scope="col">Destinations</th>
                <th scope="col">Pack status</th>
                <th scope="col">To leftover</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="skuPlan in plan.perSku"
                :key="skuPlan.sku"
                :data-testid="`disposition-preview-${skuPlan.sku}`"
              >
                <td>{{ skuPlan.sku }}</td>
                <td>{{ skuPlan.releasedQty }}</td>
                <td>{{ destinationsFor(skuPlan) }}</td>
                <td :data-testid="`disposition-pack-status-${skuPlan.sku}`">
                  <template v-if="skuPlan.pieceCount === undefined">
                    PieceCount unavailable; no PO placement
                  </template>
                  <template v-else-if="skuPlan.incompletePackQty > 0">
                    {{ skuPlan.pieceCount }}-unit packs;
                    {{ skuPlan.incompletePackQty }} incomplete to leftover
                  </template>
                  <template v-else>
                    {{ skuPlan.pieceCount }}-unit packs
                  </template>
                </td>
                <td>{{ skuPlan.leftoverQty }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <fieldset
          v-if="destination === 'targets' && plan.capacityShortfallQty > 0"
          class="qty-disposition-modal-options qty-disposition-modal-remainder"
          data-testid="disposition-remainder"
        >
          <legend>
            {{ plan.capacityShortfallQty }} unit{{
              plan.capacityShortfallQty === 1 ? "" : "s"
            }}
            remain after the selected POs' full-pack capacity is used
          </legend>
          <label
            class="qty-disposition-modal-option"
            for="qty-disposition-remainder-leftover"
          >
            <input
              id="qty-disposition-remainder-leftover"
              v-model="remainderChoice"
              type="radio"
              name="qty-disposition-remainder"
              value="leftover"
            />
            Send all remaining units to leftover inventory
          </label>
          <label
            class="qty-disposition-modal-option"
            for="qty-disposition-remainder-over-capacity"
          >
            <input
              id="qty-disposition-remainder-over-capacity"
              v-model="remainderChoice"
              type="radio"
              name="qty-disposition-remainder"
              value="over-capacity"
            />
            Place remaining complete packs in POs marked "Allow over capacity";
            incomplete packs stay leftover
          </label>
          <label
            class="qty-disposition-modal-option"
            for="qty-disposition-remainder-new-po"
          >
            <input
              id="qty-disposition-remainder-new-po"
              v-model="remainderChoice"
              type="radio"
              name="qty-disposition-remainder"
              value="new-po"
            />
            Create a new PO for remaining complete packs
          </label>
        </fieldset>
      </template>

      <section
        v-if="needsNewPo"
        class="qty-disposition-modal-new-po"
        aria-label="New PO details"
        data-testid="disposition-new-po-fields"
      >
        <h3>New PO details</h3>
        <p>
          Division {{ props.newPoDivision }} is fixed to keep this PO isolated.
          Capacity: {{ props.newPoCubeCapacityFt3.toFixed(2) }} ft³. Values
          available on the source PO are prefilled; complete any missing fields.
        </p>
        <div class="qty-disposition-modal-new-po-fields">
          <FormField label="Deliver To" required>
            <Dropdown
              :id="`${newPoFieldsId}-warehouse`"
              v-model="newPoDeliverTo"
              :options="props.newPoWarehouseOptions"
              placeholder="Select warehouse"
              :disabled="props.newPoWarehouseOptions.length === 0"
            />
          </FormField>
          <FormField label="Ship date" required>
            <DatePicker
              :id="`${newPoFieldsId}-ship-date`"
              v-model="newPoShipDate"
              placeholder="MM/DD/YYYY"
            />
          </FormField>
          <FormField label="Arrival date" required>
            <DatePicker
              :id="`${newPoFieldsId}-arrival-date`"
              v-model="newPoArrivalDate"
              placeholder="MM/DD/YYYY"
            />
          </FormField>
        </div>
        <p
          v-if="newPoMetadataError"
          class="qty-disposition-modal-hint"
          role="status"
          data-testid="disposition-new-po-hint"
        >
          {{ newPoMetadataError }}
        </p>
      </section>

      <p
        v-if="confirmDisabledReason"
        class="qty-disposition-modal-hint"
        role="status"
        data-testid="disposition-confirm-hint"
      >
        {{ confirmDisabledReason }}
      </p>
    </div>
    <div class="modal-footer qty-disposition-modal-footer">
      <Button variant="secondary" @click="handleCancel">Cancel</Button>
      <Button
        variant="primary"
        :disabled="confirmDisabledReason !== ''"
        @click="handleConfirm"
      >
        {{ confirmButtonText }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.qty-disposition-modal {
  display: flex;
  height: 100%;
  flex-direction: column;
}

.qty-disposition-modal-body {
  min-height: 0;
  flex: 1;
  overflow: auto;
}

.qty-disposition-modal-summary {
  margin: 0 0 0.8rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
}

.qty-disposition-modal-options {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0;
  border: 0;
  padding: 0;
}

.qty-disposition-modal-options legend {
  margin-bottom: 0.45rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
  font-weight: 700;
}

.qty-disposition-modal-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
}

.qty-disposition-modal-muted {
  color: var(--text-secondary, #5d6878);
}

.qty-disposition-modal-candidates {
  margin: 0.75rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.qty-disposition-modal-candidate {
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 6px;
  padding: 0.5rem 0.65rem;
}

.qty-disposition-modal-label {
  border-radius: 999px;
  background: var(--surface-muted, #eef2f8);
  padding: 0.1rem 0.5rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.72rem;
}

.qty-disposition-modal-unavailable-reasons {
  margin: 0.4rem 0 0 1.6rem;
  padding-left: 1rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.78rem;
}

.qty-disposition-modal-new-po {
  display: grid;
  gap: 0.55rem;
  margin-top: 0.8rem;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 6px;
  padding: 0.75rem;
}

.qty-disposition-modal-new-po h3,
.qty-disposition-modal-new-po p {
  margin: 0;
}

.qty-disposition-modal-new-po p {
  color: var(--text-secondary, #5d6878);
  font-size: 0.8rem;
}

.qty-disposition-modal-new-po-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.6rem;
}

.qty-disposition-modal-candidate-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
  margin: 0.35rem 0 0 1.6rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.78rem;
}

.qty-disposition-modal-ack {
  margin: 0.4rem 0 0 1.6rem;
}

.qty-disposition-modal-preview {
  margin-top: 0.9rem;
  border-top: 1px solid var(--border-subtle, #d8dee8);
  padding-top: 0.75rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.8rem;
}

.qty-disposition-modal-preview h3 {
  margin: 0 0 0.35rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
}

.qty-disposition-modal-preview-table {
  width: 100%;
  border-collapse: collapse;
}

.qty-disposition-modal-preview-table th,
.qty-disposition-modal-preview-table td {
  border-bottom: 1px solid var(--border-subtle, #d8dee8);
  padding: 0.25rem 0.4rem;
  text-align: left;
}

.qty-disposition-modal-remainder {
  margin-top: 0.9rem;
}

.qty-disposition-modal-hint {
  margin: 0.6rem 0 0;
  color: var(--danger, #b42318);
  font-size: 0.78rem;
}

.qty-disposition-modal-footer {
  flex: 0 0 auto;
}
</style>
